import Dispatcher from '../dispatcher/CentricDispatcher.react';
import { ActionTypes } from '../constants/CentricConstants';
import { ReduceStore } from 'flux/utils';
import { asDate } from '../utils/Date';

const sanitise = ({id, dateAdded, ...rest}) => ({
    id: Number(id),
    dateAdded: asDate(dateAdded),
    ...rest,
});

export const sortOptions = [
    { label: 'Name', value: 'name' },
    { label: 'Created', value: 'dateAdded' },
];

const getSortMethod = (sort) => {
    switch (sort) {
        case 'name':
            return {
                sortCriteria: (a, b) => a.value.localeCompare(b.value),
                sortMapCriteria: (el, i) => ({ index: i, value: el.name }),
            };
        case 'dateAdded':
            return {
                sortCriteria: (a, b) => (b.value - a.value),
                sortMapCriteria: (el, i) => ({ index: i, value: Date.parse(el.dateAdded) }),
            };
        default:
            return {
                sortCriteria: (a, b) => (a.value - b.value),
                sortMapCriteria: (el, i) => ({ index: i, value: el[sort] }),
            };
    }
};

const getExcludeFilter = (data) => {
    if (!data || data.length < 1) { return () => true; }
    if (!!data.id) { return (e) => !!e && e.id !== data.id; }
    if (data.length === 1) { return (e) => !!e && e.id !== data[0].id; }
    const idList = data.map(({id}) => id);
    return (e) => !!e && !idList.includes(e.id);
};

const getPayloadExcludeFilter = ({assetId = false}) =>
    (e) => (!!e && e.id !== assetId);

const isFetching = ({isFetching = 0} = {}, action) => {
    if (!action || !action.type) { return isFetching; }
    const parts = action.type.split('_');
    if (parts[0] !== 'ASSET') { return isFetching; }

    switch (parts.pop()) {
        case 'REQUEST':
            return isFetching + 1;
        case 'SUCCESS':
        case 'FAILURE':
            return isFetching - 1;
        default:
            return isFetching;
    }
};

const fetchedAll = ({ fetchedAll }, action) => {
    switch (action.type) {
        case ActionTypes.ASSET_LIST_SUCCESS:
            return action.data.length === 0;
        default:
            return fetchedAll;
    }
} ;

const defaultDraft = ({name = '', typeId = null, content = '', rawHTML = false, type = null, versionName = '', ...rest} = {}) => ({
    name,
    typeId,
    content,
    rawHTML,
    type,
    versionName,
    ...rest
});

const draft = ({draft = null}, action) => {
    switch (action.type) {
        case ActionTypes.ASSET_CREATE:
            return defaultDraft(action.payload);
        case ActionTypes.ASSET_EDIT:
            return defaultDraft(action.payload.draft);
        case ActionTypes.ASSET_UPDATE:
            return {
                ...draft,
                ...action.payload.changes
            };
        case ActionTypes.ASSET_DISCARD:
            return null;
        case ActionTypes.ASSET_ADD_SUCCESS:
            return (!!action.payload.discardOnSuccess ? null : draft);
        case ActionTypes.ASSET_UPDATE_SUCCESS:
            return (!!action.payload.discardOnSuccess ? null : draft);
        default:
            return draft;
    }
};

const assets = ({assets = []}, action) => {
    switch (action.type) {
        case ActionTypes.ASSET_INFO_SUCCESS:
            return [
                ...assets.filter(getPayloadExcludeFilter(action.payload)),
                sanitise(action.data),
            ];

        case ActionTypes.ASSET_LIST_SUCCESS:
            return [
                ...(!!action.payload.clearAll ? [] : assets.filter(getExcludeFilter(action.data))),
                ...action.data.map(sanitise)
            ];

        case ActionTypes.ASSET_ADD_SUCCESS:
            return [
                ...assets,
                sanitise(action.data),
            ];

        case ActionTypes.ASSET_UPDATE_REQUEST:
            return [
                ...assets.filter(getExcludeFilter(action.payload.current)),
                action.payload.draft,
            ];

        case ActionTypes.ASSET_UPDATE_SUCCESS:
            return [
                ...assets.filter(getExcludeFilter(action.payload.current)),
                sanitise(action.data),
            ];

        case ActionTypes.ASSET_UPDATE_FAILURE:
            return [
                ...assets.filter(getExcludeFilter(action.payload.current)),
                action.payload.current,
            ];

        case ActionTypes.ASSET_DELETE_SUCCESS:
            return [
                ...assets.filter(getExcludeFilter(action.payload.current)),
            ];

        default:
            return assets;
    }
};

const lastModifiedId = ({lastId = null} = {}, action) => {
    switch (action.type) {
        case ActionTypes.ASSET_ADD_SUCCESS:
        case ActionTypes.ASSET_UPDATE_SUCCESS:
            return action.data.id;

        case ActionTypes.ASSET_ADD_FAILURE:
        case ActionTypes.ASSET_UPDATE_FAILURE:
            return null;

        default:
            return lastId;
    }
};

class AssetStore extends ReduceStore {
    getInitialState() {
        return {
            isFetching: 0,
            fetchedAll: false,
            assets: [],
            draft: null,
            lastId: null,
        };
    }

    isFetching() {
        return this.getState().isFetching > 0;
    }

    fetchedAll() {
        return this.getState().fetchedAll;
    }

    getLastId() {
        return this.getState().lastId;
    }

    listAll(params = {}) {
        if (!params.sort || this._state.assets.length <= 1 ) { return this.getState().assets; }
        const sort = getSortMethod(params.sort);
        const assets = this.getState().assets;
        const map = assets.map(sort.sortMapCriteria).sort(sort.sortCriteria);
        return map.map(({index}) => assets[index]);
    }

    getById(id) {
        return this.getState().assets.find(e => !!e && e.id === id);
    }

    getByName(name) {
        return this.getState().assets.find(e => !!e && e.name === name);
    }

    search(input) {
        const lcInput = input.toLocaleLowerCase();
        return this.getState().assets.filter(({name}) => -1 < name.toLocaleLowerCase().search(lcInput));
    }

    getDraft() {
        return this.getState().draft;
    }

    reduce(state, action) {
        return {
            isFetching: isFetching(state, action),
            fetchedAll: fetchedAll(state, action),
            assets: assets(state, action),
            draft: draft(state, action),
            lastId: lastModifiedId(state, action),
        };
    }
}
export default new AssetStore(Dispatcher);
